import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  TextareaAutosize,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import { createFeatureRequestBannerStyles } from "./styles";

/**
 * Props for the FeatureRequestSubmission component
 */
interface FeatureRequestSubmissionProps {
  /** Callback function to handle feature request submission */
  onSubmit: (title: string, description: string, userId: string) => void;
  /** Whether the form is currently being submitted */
  isSubmitting: boolean;
  /** Whether the form has been submitted */
  hasSubmitted: boolean;
  /** The current user's ID */
  userId: string;
  /** Error message to display, if any */
  error: string | null;
}

/**
 * FeatureRequestSubmission Component
 *
 * Provides a form for users to submit new feature requests with title and description fields.
 * Includes character count validation, form submission handling, and success feedback.
 * Features reduced textarea height to ensure submit button is visible without scrolling.
 */
const FeatureRequestSubmission: React.FC<FeatureRequestSubmissionProps> = ({
  onSubmit,
  isSubmitting,
  hasSubmitted,
  userId,
  error,
}) => {
  const theme = useTheme();
  const featureRequestBannerStyles = createFeatureRequestBannerStyles(theme);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const TITLE_MAX_LENGTH = 50;
  const DESCRIPTION_MAX_LENGTH = 100;

  /**
   * Handles changes to the title input field with character limit validation
   */
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= TITLE_MAX_LENGTH) {
      setTitle(value);
    }
  };

  /**
   * Handles changes to the description textarea with character limit validation
   */
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= DESCRIPTION_MAX_LENGTH) {
      setDescription(value);
    }
  };

  /**
   * Handles form submission with validation and cleanup
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) return;

    await onSubmit(title, description, userId);

    if (!isSubmitting) {
      setTitle("");
      setDescription("");
    }
  };

  return (
    <Box sx={featureRequestBannerStyles.submissionContainer}>
      <Box sx={featureRequestBannerStyles.submissionWrapper}>
        {/* Title */}
        <Typography
          variant="h6"
          sx={featureRequestBannerStyles.submissionTitle}
        >
          Submit a Feature Request
        </Typography>
        {/* Error */}
        {error && (
          <Typography sx={featureRequestBannerStyles.submissionError}>
            <Box
              component="span"
              sx={featureRequestBannerStyles.submissionErrorIcon}
            >
              *
            </Box>
            {error}
          </Typography>
        )}
        {/* Form */}
        <Box sx={featureRequestBannerStyles.submissionContent}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={featureRequestBannerStyles.submissionForm}
          >
            <Box sx={featureRequestBannerStyles.submissionFields}>
              <Box sx={featureRequestBannerStyles.submissionFieldWrapper}>
                {/* Title */}
                <TextField
                  type="text"
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Title your feature request"
                  required
                  fullWidth
                  size="small"
                  sx={featureRequestBannerStyles.submissionTitleField}
                />
                <Typography
                  sx={{
                    ...featureRequestBannerStyles.submissionCharCount,
                    ...featureRequestBannerStyles.submissionCharCountTitle,
                  }}
                >
                  {title.length}/{TITLE_MAX_LENGTH}
                </Typography>
              </Box>
              <Box sx={featureRequestBannerStyles.submissionFieldWrapper}>
                {/* Description */}
                <TextField
                  multiline
                  rows={1}
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Describe your feature request"
                  required
                  fullWidth
                  size="small"
                  sx={featureRequestBannerStyles.submissionDescriptionField}
                />
                <Typography
                  sx={{
                    ...featureRequestBannerStyles.submissionCharCount,
                    ...featureRequestBannerStyles.submissionCharCountDescription,
                  }}
                >
                  {description.length}/{DESCRIPTION_MAX_LENGTH}
                </Typography>
              </Box>
              <Box sx={featureRequestBannerStyles.submissionFieldWrapper}>
                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  disabled={isSubmitting || hasSubmitted}
                  sx={featureRequestBannerStyles.submissionButton}
                  disableRipple
                >
                  {isSubmitting || hasSubmitted ? (
                    <CheckIcon
                      sx={featureRequestBannerStyles.submissionButtonIcon}
                    />
                  ) : (
                    "Submit Request"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FeatureRequestSubmission;
